/**
 * My assets — the pure view model.
 *
 * The page offers three views of ONE list: grouped by stage, grouped by the
 * Client Acquisition System, and a flat library. Nothing about an asset changes
 * between them — the same rows, the same statuses, the same click targets — so
 * every grouping, count, sort and filter lives here as a plain function over
 * the server's `CoachAssetView[]`. The components below it only lay the answer
 * out.
 *
 * Deliberately free of React and of the DOM, so each rule is testable on its
 * own and a component cannot quietly grow a second idea of what "built" means.
 *
 * Aliases: my assets view model, asset grouping, asset views, asset chips
 */

import { format } from "date-fns";

import type {
  CoachAssetDetail,
  CoachAssetNote,
  CoachAssetView,
  CoachCollectionItem,
  StartAssetRefusal,
} from "~/lib/functions/nova-assets";
import { ContentStatus } from "~/lib/schemas/db-enums-cih";
import type { CoachLifecycle } from "~/lib/shared/coach-account";
import type { NamedFeatureKey } from "~/lib/shared/features/keys";
import type {
  AssetKey,
  AssetStage,
  CasComponentKey,
  CollectionAssetKey,
} from "~/lib/shared/nova/assets/catalog";
import {
  ASSET_STAGE_ORDER,
  CAS_COMPONENTS,
  assetByKey,
  assetsFedBy,
  hasApprovalStep,
  hasAssetPage,
  isCollectionAssetKey,
  isRoutineAsset,
} from "~/lib/shared/nova/assets/catalog";
import {
  postOpeningLine,
  truncateTitle,
} from "~/lib/shared/nova/assets/collection-item";
import type {
  AssetActivity,
  AssetStatus,
  AssetTarget,
} from "~/lib/shared/nova/assets/status";
import { WINBACK_TRIAL_CTA } from "~/lib/shared/plan-change";

/** Which of the three arrangements the coach is looking at. */
export type AssetViewId = "stage" | "cas" | "library";

export const ASSET_VIEWS = [
  { id: "stage", label: "Grouped by Stage" },
  { id: "cas", label: "Client Acquisition System" },
  { id: "library", label: "Full library" },
] as const satisfies readonly { id: AssetViewId; label: string }[];

/** Narrows a stored preference — the validation boundary for localStorage. */
export function isAssetViewId(value: string): value is AssetViewId {
  return ASSET_VIEWS.some((view) => view.id === value);
}

export const ASSET_STAGE_LABELS: Record<AssetStage, string> = {
  diagnose: "Diagnose",
  plan: "Plan",
  build: "Build",
  grow: "Grow",
};

/** Chip colour families, named by meaning rather than by hue. */
export type AssetChipTone =
  | "approved"
  | "attention"
  | "progress"
  | "danger"
  | "muted"
  | "outline";

export interface AssetStatusPresentation {
  readonly label: string;
  readonly tone: AssetChipTone;
  /** Draw a lock beside the label — the held, still-saved work of a lapse. */
  readonly lock: boolean;
}

/**
 * Status → what the coach reads. The `satisfies` is the guard: a status added
 * to `AssetStatus` fails to compile here until it is given a label and a tone,
 * so no surface can invent its own wording for it.
 */
export const ASSET_STATUS_PRESENTATION = {
  approved: {
    label: "Approved",
    tone: "approved",
    lock: false,
  },
  needs_review: {
    label: "Needs review",
    tone: "attention",
    lock: false,
  },
  draft: { label: "Draft", tone: "progress", lock: false },
  in_progress: {
    label: "In progress",
    tone: "progress",
    lock: false,
  },
  failed: {
    label: "Needs a retry",
    tone: "danger",
    lock: false,
  },
  next: { label: "Next", tone: "attention", lock: false },
  not_started: {
    label: "Not started",
    tone: "muted",
    lock: false,
  },
  not_yet: { label: "Not yet", tone: "muted", lock: false },
  with_your_trial: {
    label: "With your trial",
    tone: "muted",
    lock: false,
  },
  locked_churned: {
    label: "Saved",
    tone: "muted",
    lock: true,
  },
  coming_soon: {
    label: "Coming soon",
    tone: "outline",
    lock: false,
  },
} as const satisfies Record<AssetStatus, AssetStatusPresentation>;

/** A churned coach's locked chip, in the win-back panel's own words. */
const CHURNED_LOCK_PRESENTATION: Record<
  "kept" | "frozen",
  AssetStatusPresentation
> = {
  kept: { label: "Kept", tone: "approved", lock: true },
  frozen: { label: "Frozen", tone: "muted", lock: true },
};

/**
 * A churned coach's locked chip: Kept or Frozen when her `note` says which
 * (the same evidence and rule the win-back panel groups by), the not-started
 * look with a lock when the note is explicitly `null` — she never touched
 * the asset, so a locked card must not claim work that was never done — or
 * the generic locked default when the caller passes no `note` at all
 * (surfaces outside My assets, which have never read one).
 */
function lockedChurnedPresentation(
  note: CoachAssetNote | null | undefined,
): AssetStatusPresentation {
  if (note?.kind === "churned") return CHURNED_LOCK_PRESENTATION[note.state];
  if (note === null) {
    return { ...ASSET_STATUS_PRESENTATION.not_started, lock: true };
  }
  return ASSET_STATUS_PRESENTATION.locked_churned;
}

/**
 * The feature a Coming soon asset stands for, or null for every other asset
 * and status. Its card opens the feature's vote dialog.
 */
export function comingSoonVoteKey(
  status: AssetStatus,
  assetKey: AssetKey,
): NamedFeatureKey | null {
  return status === "coming_soon" ? assetByKey(assetKey).featureKey : null;
}

/**
 * What the coach reads for one asset's status. An asset with no approval step
 * (`hasApprovalStep`) was saved as given, never approved, so its `approved`
 * status reads "Saved", as its status line and its Details do, and an open
 * routine reads "Weekly". A churned
 * coach's `locked_churned` status reads through
 * {@link lockedChurnedPresentation}. `note` is optional because only the
 * surface that shows it (My assets) needs to pass it.
 */
export function assetStatusPresentation(
  status: AssetStatus,
  assetKey: AssetKey,
  note?: CoachAssetNote | null,
): AssetStatusPresentation {
  if (status === "locked_churned") return lockedChurnedPresentation(note);
  const presentation = ASSET_STATUS_PRESENTATION[status];
  if (status !== "approved") return presentation;
  const entry = assetByKey(assetKey);
  // An open routine is something she does every week, not a finished asset.
  if (entry.tracks.kind === "routine") {
    return { ...presentation, label: "Weekly", tone: "attention" };
  }
  return hasApprovalStep(entry)
    ? presentation
    : { ...presentation, label: "Saved" };
}

/**
 * What is happening to an asset that already has a version. The chip keeps
 * saying where the asset stands; this takes the line under it. `working` can
 * mean only that a conversation is open on the asset, with no run in it, so
 * the line does not claim NOVA is producing anything.
 */
const ASSET_ACTIVITY_NOTES = {
  working: "Being revised with NOVA",
  failed: "The last attempt stopped. Open it to try again.",
} as const satisfies Record<AssetActivity, string>;

/**
 * The line under the status.
 *
 * Activity comes first: work in flight on an asset is news, and its date is
 * not. A dated note reads differently depending on what the date means — the
 * day a draft landed, the day work started, the day a run failed — so the
 * status picks the verb. An approved asset says nothing more than "Approved".
 *
 * `hydrated` is the caller's answer to "is the browser reading this yet?". The
 * day is formatted in the reader's own timezone, which the server cannot know,
 * so before hydration a dated note has no text at all rather than a day the
 * client would then disagree with.
 */
export function assetNoteText(
  asset: Pick<CoachAssetView, "status" | "note" | "activity">,
  hydrated: boolean,
): string | null {
  const { status, note, activity } = asset;
  if (activity !== null) return ASSET_ACTIVITY_NOTES[activity];
  if (note === null) return null;
  // The chip already says Kept or Frozen; the win-back panel, not this line,
  // is where the coach reads what that work is.
  if (note.kind === "churned") return null;
  if (note.kind !== "updated") return note.text;
  if (!hydrated) return null;

  const at = format(new Date(note.at), "MMM d");
  switch (status) {
    case "approved":
      return null;
    case "needs_review":
      return `Drafted ${at}`;
    case "draft":
      return `Started ${at}`;
    case "failed":
      return `Last tried ${at}`;
    default:
      return `Updated ${at}`;
  }
}

/**
 * Built, whatever the lifecycle: approved now, or — for a churned coach,
 * whose own status reads `locked_churned` — approved before her access
 * lapsed. The win-back panel's Kept group is the same fact; "N of M built"
 * must count it the same way, or the two surfaces tell different stories
 * about the same asset.
 */
function isBuilt(asset: Pick<CoachAssetView, "status" | "note">): boolean {
  return (
    asset.status === "approved" ||
    (asset.note?.kind === "churned" && asset.note.state === "kept")
  );
}

export interface AssetCounts {
  /** Approved — see {@link isBuilt}. */
  readonly built: number;
  /** Everything that exists today; coming-soon entries and routines are not countable. */
  readonly live: number;
}

export function countAssets(assets: readonly CoachAssetView[]): AssetCounts {
  let built = 0;
  let live = 0;
  for (const asset of assets) {
    // A routine is run every week, never built, so it counts toward neither.
    if (asset.status === "coming_soon" || isRoutineAsset(asset.key)) continue;
    live += 1;
    if (isBuilt(asset)) built += 1;
  }
  return { built, live };
}

export interface AssetStageGroup {
  readonly stage: AssetStage;
  readonly label: string;
  /** 1–4, the column's badge. */
  readonly position: number;
  readonly assets: readonly CoachAssetView[];
  readonly counts: AssetCounts;
}

const STAGES_IN_ORDER: readonly AssetStage[] = (
  Object.keys(ASSET_STAGE_ORDER) as AssetStage[]
).sort((a, b) => ASSET_STAGE_ORDER[a] - ASSET_STAGE_ORDER[b]);

/**
 * The four stage columns. Only the My assets surface — content assets have a
 * page of their own and would make the stage counts disagree with the header.
 */
export function groupByStage(
  assets: readonly CoachAssetView[],
): readonly AssetStageGroup[] {
  return STAGES_IN_ORDER.map((stage, index) => {
    const inStage = assets.filter(
      (asset) => asset.surface === "assets" && asset.stage === stage,
    );
    return {
      stage,
      label: ASSET_STAGE_LABELS[stage],
      position: index + 1,
      assets: inStage,
      counts: countAssets(inStage),
    };
  });
}

export interface AssetCasGroup {
  readonly component: CasComponentKey;
  readonly label: string;
  /** Null for the two groups outside the five-week sequence. */
  readonly courseWeek: number | null;
  readonly assets: readonly CoachAssetView[];
  readonly approved: number;
  readonly live: number;
}

const CAS_KEYS_IN_ORDER = Object.keys(CAS_COMPONENTS) as CasComponentKey[];

/**
 * The course's own grouping: five numbered components, then the two unnumbered
 * ones. Coming-soon assets are left out entirely rather than shown as future
 * members — a component's progress is a promise about work that can be done
 * today.
 */
export function groupByCasComponent(
  assets: readonly CoachAssetView[],
): readonly AssetCasGroup[] {
  return CAS_KEYS_IN_ORDER.flatMap((component) => {
    const members = assets.filter(
      (asset) =>
        asset.component === component && asset.status !== "coming_soon",
    );
    if (members.length === 0) return [];
    return [
      {
        component,
        label: CAS_COMPONENTS[component].label,
        courseWeek: CAS_COMPONENTS[component].courseWeek,
        assets: members,
        approved: members.filter(isBuilt).length,
        live: members.length,
      },
    ];
  });
}

export interface CasComponentProgress {
  readonly component: CasComponentKey;
  readonly label: string;
  readonly courseWeek: number;
  readonly approved: number;
  readonly live: number;
  /** 0–100, rounded. */
  readonly percent: number;
}

/** The five numbered components, for the panel beside the intro. */
export function casComponentProgress(
  assets: readonly CoachAssetView[],
): readonly CasComponentProgress[] {
  return groupByCasComponent(assets).flatMap((group) =>
    group.courseWeek === null
      ? []
      : [
          {
            component: group.component,
            label: group.label,
            courseWeek: group.courseWeek,
            approved: group.approved,
            live: group.live,
            percent:
              group.live === 0
                ? 0
                : Math.round((group.approved / group.live) * 100),
          },
        ],
  );
}

/**
 * Reading order for the flat library: what wants the coach now, then what is
 * underway, then what is done, then what is not reachable yet. `in_progress`
 * and `failed` sit beside `draft` because all three are started work.
 */
export const LIBRARY_STATUS_ORDER = {
  needs_review: 0,
  next: 1,
  in_progress: 2,
  draft: 3,
  failed: 4,
  approved: 5,
  not_started: 6,
  not_yet: 7,
  with_your_trial: 8,
  locked_churned: 9,
  coming_soon: 10,
} as const satisfies Record<AssetStatus, number>;

export type LibraryFilter = "all" | "in_progress" | "needs_review" | "approved";

export const LIBRARY_FILTERS = [
  { id: "all", label: "All" },
  { id: "in_progress", label: "In progress" },
  { id: "needs_review", label: "Needs review" },
  { id: "approved", label: "Approved" },
] as const satisfies readonly { id: LibraryFilter; label: string }[];

const FILTER_STATUSES: Record<
  Exclude<LibraryFilter, "all">,
  readonly AssetStatus[]
> = {
  // Started work that still wants the coach — `failed` included, for the same
  // reason the sort groups it with `draft`: a run that fell over is work in
  // hand, and a filter that hid it would leave the retry reachable only from
  // "All". An asset with a version reads its own status even while it is
  // being revised or its revision failed, so `matchesLibraryFilter` adds those
  // through their activity.
  in_progress: ["draft", "next", "in_progress", "failed"],
  needs_review: ["needs_review"],
  approved: ["approved"],
};

/**
 * An asset with activity is started work as well as whatever its status says,
 * so it shows under "In progress" and under its own status's filter.
 */
function matchesLibraryFilter(
  asset: CoachAssetView,
  filter: Exclude<LibraryFilter, "all">,
): boolean {
  // An open routine reads `approved`, but there is nothing to approve in it.
  if (filter === "approved" && isRoutineAsset(asset.key)) return false;
  if (FILTER_STATUSES[filter].includes(asset.status)) return true;
  return filter === "in_progress" && asset.activity !== null;
}

/**
 * The flat list. Content assets join it once the coach's plan opens them — in
 * her trial or on a paid plan — because a row a coach cannot reach is noise on
 * the one view whose job is to be complete.
 *
 * The sort is stable by construction: equal statuses keep catalog order,
 * because the index is the tiebreak rather than a hope about `Array.sort`.
 */
export function buildLibraryRows(
  assets: readonly CoachAssetView[],
  filter: LibraryFilter,
  lifecycle: CoachLifecycle,
): readonly CoachAssetView[] {
  const visible = assets.filter(
    (asset) =>
      asset.surface === "assets" ||
      (asset.surface === "content" &&
        (lifecycle === "trial" || lifecycle === "paid")),
  );
  const kept =
    filter === "all"
      ? visible
      : visible.filter((asset) => matchesLibraryFilter(asset, filter));

  return kept
    .map((asset, index) => ({ asset, index }))
    .sort(
      (a, b) =>
        LIBRARY_STATUS_ORDER[a.asset.status] -
          LIBRARY_STATUS_ORDER[b.asset.status] || a.index - b.index,
    )
    .map((entry) => entry.asset);
}

/**
 * A coach who has not started a trial gets one arrangement and no switch: the
 * stage columns are the story of what NOVA builds, and the other two views
 * answer questions they do not have yet.
 */
export function canSwitchAssetView(lifecycle: CoachLifecycle): boolean {
  return lifecycle !== "pre_trial";
}

/** The view to render — the lifecycle decides first, the preference second. */
export function resolveAssetView(
  lifecycle: CoachLifecycle,
  preference: AssetViewId | null,
): AssetViewId {
  return canSwitchAssetView(lifecycle) ? (preference ?? "stage") : "stage";
}

type PlanStepRefusalReason = NonNullable<
  StartAssetRefusal["planStep"]
>["reason"];
type SkillRefusalReason = NonNullable<StartAssetRefusal["skill"]>["reason"];
type ItemRefusalReason = NonNullable<StartAssetRefusal["item"]>;

/**
 * The two gates behind the catalog, in the coach's words.
 *
 * Neither gate's own text can go on a card. The Plan router carries no prose at
 * all — only a reason code — and the skill registry's `message` is written AT
 * the model ("Tell the coach plainly, don't start the work…"). So each reason
 * gets one plain line here, and `satisfies` keeps the maps total: a refusal
 * reason added upstream fails to compile until it has coach-facing words.
 */
const PLAN_STEP_REFUSAL_NOTES = {
  step_disabled: "This step of your Plan isn't open yet.",
  order_blocked: "Finish the earlier steps of your Plan first.",
  verdict_required: "Run your market check first.",
  verdict_stale: "Your ideal client changed — re-run the market check first.",
  destination_check_required: "Your market check needs its destination check.",
  starting_rung_check_required:
    "Your market check needs its starting-rung check.",
  starting_rung_check_failed:
    "The starting-rung check didn't pass — revisit your market check.",
  verdict_weak: "Your market check came back weak — sharpen it first.",
  verdict_kill: "Your market check didn't pass — revisit it first.",
} as const satisfies Record<PlanStepRefusalReason, string>;

const SKILL_REFUSAL_NOTES = {
  unknown_skill: "Nova can't open this one yet.",
  access_denied: "This one isn't open on your plan yet.",
  conversation_complete: "Start a new Nova conversation for this one.",
  missing_prerequisite: "Nova needs an earlier asset finished first.",
} as const satisfies Record<SkillRefusalReason, string>;

/**
 * From a post's own page, the only post NOVA cannot find to revise is one
 * saved before each post had its own history. Approving or editing it once
 * gives it one.
 */
const ITEM_REFUSAL_NOTES = {
  not_found:
    "This post can't be revised yet. Approve or edit it once, then ask NOVA again.",
  not_revisable: "NOVA revises this asset as a whole, not one item at a time.",
} as const satisfies Record<ItemRefusalReason, string>;

/**
 * What a refused start says on the card, in the card's own vocabulary. A
 * refusal only ever arrives from a click, so the browser is reading it.
 *
 * The order is the order the server refuses in: the item to revise is checked
 * first, the quota is about the day, the two gates are about work the coach
 * still owes, and only then does the asset's own status get to speak. Without
 * the gate branches a Mini Sprint that cleared the catalog and failed the
 * skill's prerequisite would read back the ordinary estimate — an answer to a
 * question the coach did not ask.
 */
export function assetRefusalMessage(refusal: StartAssetRefusal): string {
  if (refusal.item) return ITEM_REFUSAL_NOTES[refusal.item];
  if (refusal.quotaReason) {
    return "You've reached today's Nova limit. Try again tomorrow.";
  }
  if (refusal.planStep) {
    return PLAN_STEP_REFUSAL_NOTES[refusal.planStep.reason];
  }
  if (refusal.skill) {
    return SKILL_REFUSAL_NOTES[refusal.skill.reason];
  }
  // A refusal carries no activity: it answers why the start was refused.
  return (
    assetNoteText(
      { status: refusal.status, note: refusal.note, activity: null },
      true,
    ) ?? "Nova can't open this one yet."
  );
}

/**
 * A refusal is the asset resolved a moment ago: applying its status, note and
 * target in place of the list row's own keeps a surface from repeating a
 * click the server just declined. It carries no activity, so the row's older
 * one is not claimed either. Shared by every surface that turns a refusal
 * back into an asset to render — the card (`AssetActionShell`) and the asset
 * page.
 */
export function applyAssetRefusal(
  asset: CoachAssetView,
  refusal: StartAssetRefusal | null,
): CoachAssetView {
  if (!refusal) return asset;
  return {
    ...asset,
    status: refusal.status,
    note: refusal.note,
    target: refusal.target,
    activity: null,
  };
}

/**
 * What the coach's note asks of NOVA: changes to a version waiting for review,
 * or a revision of an approved one.
 */
export type AssetChangeRequest = "ask_for_changes" | "revise";

/**
 * The change form, per request. Asking for changes needs the note, because it
 * is the whole message; a revision can start from NOVA's own opening.
 */
export const ASSET_CHANGE_REQUESTS = {
  ask_for_changes: {
    label: "Ask for changes",
    submitLabel: "Send to NOVA",
    noteRequired: true,
  },
  revise: {
    label: "Revise with NOVA",
    submitLabel: "Revise with NOVA",
    noteRequired: false,
  },
} as const satisfies Record<
  AssetChangeRequest,
  { label: string; submitLabel: string; noteRequired: boolean }
>;

/**
 * Open the change form. The note goes to the live conversation when there is
 * one, and rides a start (`useStartAsset`) when there is not.
 */
export interface AssetChangeAction {
  readonly kind: "change";
  readonly request: AssetChangeRequest;
  readonly conversationId: string | null;
}

/** The one thing the asset page offers the coach to do next. */
export type AssetPageAction =
  /** Start or restart the work with NOVA (`useStartAsset`). */
  | { readonly kind: "start"; readonly label: string }
  /** Open the live conversation as it is. Nothing is sent. */
  | {
      readonly kind: "conversation";
      readonly label: string;
      readonly conversationId: string;
    }
  /** Approve the version on screen (`useQueueItemReview`). */
  | { readonly kind: "approve" }
  | AssetChangeAction
  /** Open the asset this one is waiting on. */
  | {
      readonly kind: "open_asset";
      readonly label: string;
      readonly asset: AssetKey;
    }
  /** Follow the card's own target: trial, win-back or billing. */
  | { readonly kind: "target"; readonly label: string };

/** What the page can say and offer once it knows what the asset holds. */
export interface AssetPageReady {
  readonly kind: "ready";
  /** Where the asset stands, in one sentence. */
  readonly line: string;
  /** What NOVA is doing with a version that already exists. */
  readonly activity: string | null;
  readonly primary: AssetPageAction | null;
  /** Asking for changes beside Approve, for a version waiting for review. */
  readonly secondary: AssetChangeAction | null;
  /** The status allows editing. The review hook still decides for the version. */
  readonly edit: boolean;
  /** The asset is closed to the coach, so its content stays off the page. */
  readonly locked: boolean;
}

/**
 * The page's plan. An open asset's line and action turn on what the detail
 * read returns, so until it has, the page offers nothing: an action guessed
 * from missing review data would start new work on a version that is waiting
 * for approval.
 */
export type AssetPagePlan =
  | { readonly kind: "pending" }
  | { readonly kind: "failed" }
  | AssetPageReady;

/** Where the page's own read of the asset stands. */
export type AssetDetailState = AssetPagePlan["kind"];

/**
 * The plan's facts, from the asset's list row and its detail read. The asset
 * page and the session rail both build them here, so one asset cannot read
 * two ways.
 */
export function assetPageFacts(input: {
  readonly asset: CoachAssetView;
  readonly detail: CoachAssetDetail | null;
  readonly detailState: AssetDetailState;
  /** The shown version waits on the coach (`useQueueItemReview`). */
  readonly reviewable: boolean;
  /** Dates are formatted only once hydrated, so SSR and the client agree. */
  readonly hydrated: boolean;
}): AssetPageFacts {
  const { asset, detail, hydrated } = input;
  const entry = assetByKey(asset.key);
  const approvedAt = detail?.shown?.approvedAt ?? null;
  return {
    status: asset.status,
    detail: input.detailState,
    activity: asset.activity,
    target: asset.target,
    note: asset.note,
    estimateMinutes: entry.estimateMinutes,
    shownVersion: detail?.shown?.version ?? null,
    liveVersion: detail?.live?.version ?? null,
    approvedOn:
      approvedAt !== null && hydrated
        ? format(new Date(approvedAt), "MMM d, yyyy")
        : null,
    conversationId: detail?.conversation?.id ?? null,
    reviewable: input.reviewable,
    reviewed: hasApprovalStep(entry),
    feedsOthers: assetsFedBy(asset.key).length > 0,
  };
}

/** What the page knows about one asset, reduced to what its actions turn on. */
export interface AssetPageFacts {
  readonly status: AssetStatus;
  /** The detail read behind the version, the conversation and the review. */
  readonly detail: AssetDetailState;
  readonly activity: AssetActivity | null;
  readonly target: AssetTarget;
  readonly note: CoachAssetNote | null;
  readonly estimateMinutes: number | null;
  /** The version on the page, or null when there is none to number. */
  readonly shownVersion: number | null;
  /** The approved version other assets keep using while another is shown. */
  readonly liveVersion: number | null;
  /** The approval day, formatted, or null when unknown or not hydrated yet. */
  readonly approvedOn: string | null;
  /** The newest live conversation working on the asset. */
  readonly conversationId: string | null;
  /** The shown version waits on the coach, so Approve can act on it. */
  readonly reviewable: boolean;
  /**
   * The asset is approved through review (`hasApprovalStep`). False for a Plan
   * step's verdict and for answers saved straight from the coach, which are
   * never reviewed.
   */
  readonly reviewed: boolean;
  /** Other assets read from this one, so its approved version is theirs too. */
  readonly feedsOthers: boolean;
}

type TargetWithCta = Extract<
  AssetTarget,
  {
    kind: "winback" | "winback_trial" | "billing" | "explain_trial";
  }
>["kind"];

const TARGET_CTA_LABELS = {
  winback: "Come back to NOVA",
  winback_trial: WINBACK_TRIAL_CTA,
  billing: "Settle your invoice",
  explain_trial: "Start my 7-day free trial",
} as const satisfies Record<TargetWithCta, string>;

/**
 * The call to action of a target that unlocks the asset, or null for a target
 * that is already open (`open_asset`, `own_page`) or has nowhere to click
 * (`none`). Shared by the page's own plan and by what a refused start swaps
 * in, so the two cannot invent different words for the same target.
 */
export function targetCta(target: AssetTarget): string | null {
  switch (target.kind) {
    case "winback":
    case "winback_trial":
    case "billing":
    case "explain_trial":
      return TARGET_CTA_LABELS[target.kind];
    case "open_asset":
    case "own_page":
    case "none":
      return null;
    default: {
      const exhaustive: never = target;
      throw new Error(`Unknown asset target: ${JSON.stringify(exhaustive)}`);
    }
  }
}

/** The way into a closed asset: its target, or the asset it waits on. */
export type AssetWayIn = Extract<
  AssetPageAction,
  { kind: "target" | "open_asset" }
>;

/** A `not_yet` asset's line, and the asset it waits on when that has a page. */
function notYetPlan(note: CoachAssetNote | null): {
  readonly line: string;
  readonly action: AssetWayIn | null;
} {
  if (note?.kind !== "after_dependency") {
    return {
      line: "Opens after the assets it builds on are approved.",
      action: null,
    };
  }
  const { dependency, dependencyLabel } = note;
  const dependencyEntry = assetByKey(dependency);
  // A dependency with no approval step is completed, never approved.
  const done = hasApprovalStep(dependencyEntry) ? "approved" : "completed";
  return {
    line: `Opens after your ${dependencyLabel} is ${done}.`,
    action: hasAssetPage(dependencyEntry)
      ? {
          kind: "open_asset",
          label: `Open ${dependencyLabel}`,
          asset: dependency,
        }
      : null,
  };
}

/**
 * A start refused over what the coach's ACCOUNT may open — trial-only, or
 * held by a churned subscription — rather than something about this one
 * asset: a Plan-step order gate, a skill prerequisite, today's turn quota, an
 * unmet dependency. Those still refuse `status`, but never with one of these
 * two, and their target is always `none` (`refuseWithoutTarget` on
 * the server), so checking the target's own CTA catches the same set from the
 * other side.
 *
 * The distinction matters for cached pages: an access refusal answers for the
 * whole account, so every other asset's cached detail is wrong too, not just
 * the one that was clicked.
 */
export function isAccessRefusal(refusal: StartAssetRefusal): boolean {
  switch (refusal.status) {
    case "with_your_trial":
    case "locked_churned":
      return true;
    default:
      return targetCta(refusal.target) !== null;
  }
}

/**
 * The one action a refused start leaves the page: the same CTA a closed card
 * shows for the refusal's own target, or none once the refusal already
 * explains itself with nowhere to send the coach — a Plan-step or skill gate,
 * whose target is always `none`.
 */
export function refusedAssetAction(
  target: AssetTarget,
): AssetPageAction | null {
  const cta = targetCta(target);
  return cta === null ? null : { kind: "target", label: cta };
}

function lockedLine(status: AssetStatus, target: AssetTarget): string {
  switch (status) {
    case "with_your_trial":
      return "NOVA builds this one with you during your trial.";
    case "locked_churned":
      return target.kind === "billing"
        ? "Your work is saved. It opens again once your invoice is settled."
        : target.kind === "winback_trial"
          ? "Your work is saved. It opens again when you come back to NOVA, and your first 7 days on NOVA Core are free."
          : "Your work is saved. It opens again when you come back to NOVA.";
    case "coming_soon":
      return "Coming soon.";
    default:
      return "This is not open on your plan yet.";
  }
}

/**
 * The asset page's status line and actions, from one status.
 *
 * One primary action per status, so the coach is never asked to choose
 * between two ways forward. The one secondary, Ask for changes beside
 * Approve, is the other answer to the same review. A locked asset takes the
 * card's own target — the page and the card must not disagree about what
 * unlocks it — and needs no detail, because its content stays off the page.
 * Every open asset waits for the detail read. Once a version exists, what
 * NOVA is doing with it is a second line, never the status.
 */
export function assetPagePlan(facts: AssetPageFacts): AssetPagePlan {
  const settle = (
    line: string,
    primary: AssetPageAction | null,
    extra: Partial<
      Pick<AssetPageReady, "activity" | "secondary" | "edit" | "locked">
    > = {},
  ): AssetPageReady => ({
    kind: "ready",
    line,
    primary,
    secondary: null,
    activity: null,
    edit: false,
    locked: false,
    ...extra,
  });
  const withNova = (label: string): AssetPageAction =>
    facts.conversationId === null
      ? { kind: "start", label }
      : { kind: "conversation", label, conversationId: facts.conversationId };
  const change = (request: AssetChangeRequest): AssetChangeAction => ({
    kind: "change",
    request,
    conversationId: facts.conversationId,
  });
  const carryOn = (): AssetPageAction =>
    facts.conversationId === null
      ? { kind: "start", label: "Continue with NOVA" }
      : {
          kind: "conversation",
          label: "Continue the conversation",
          conversationId: facts.conversationId,
        };
  const activity =
    facts.activity === null ? null : ASSET_ACTIVITY_NOTES[facts.activity];

  const cta = targetCta(facts.target);
  if (cta !== null) {
    return settle(
      lockedLine(facts.status, facts.target),
      { kind: "target", label: cta },
      { locked: true },
    );
  }

  switch (facts.status) {
    case "coming_soon":
    case "with_your_trial":
    case "locked_churned":
      return settle(lockedLine(facts.status, facts.target), null, {
        locked: true,
      });
    default:
      break;
  }

  if (facts.detail !== "ready") return { kind: facts.detail };

  switch (facts.status) {
    case "not_yet": {
      const { line, action } = notYetPlan(facts.note);
      return settle(line, action);
    }

    case "next":
    case "not_started":
      return settle(
        facts.estimateMinutes === null
          ? "Not written yet."
          : `Not written yet. About ${facts.estimateMinutes} minutes with NOVA.`,
        { kind: "start", label: "Start with NOVA" },
      );

    case "failed":
      return settle("The last attempt stopped.", {
        kind: "start",
        label: "Try again",
      });

    case "in_progress":
      return settle(
        facts.conversationId === null
          ? "You started this with NOVA."
          : "NOVA is building this with you.",
        carryOn(),
      );

    case "draft":
      if (facts.shownVersion === null) {
        return settle(
          facts.conversationId === null
            ? "You started this with NOVA."
            : "NOVA is building this with you.",
          carryOn(),
        );
      }
      return settle(`Version ${facts.shownVersion} is a draft.`, carryOn(), {
        activity,
      });

    case "needs_review": {
      if (facts.reviewable && facts.shownVersion !== null) {
        const live =
          facts.liveVersion === null
            ? ""
            : ` Version ${facts.liveVersion} stays live until then.`;
        return settle(
          `Version ${facts.shownVersion} is ready. Nothing uses it until you approve it.${live}`,
          { kind: "approve" },
          { activity, secondary: change("ask_for_changes"), edit: true },
        );
      }
      // Nothing here to approve: a verdict bound to an older ideal client, or
      // content that moved on since its approval. NOVA has to bring it up to
      // date.
      return facts.reviewed
        ? settle(
            "This changed since you approved it. Go through it again with NOVA.",
            change("revise"),
            { activity },
          )
        : settle(
            "This check is out of date. Something it builds on changed since it ran.",
            withNova("Run it again with NOVA"),
            { activity },
          );
    }

    case "approved": {
      const when = facts.approvedOn === null ? "" : ` on ${facts.approvedOn}`;
      const line = facts.reviewed
        ? `Approved${when}.${facts.feedsOthers ? " Your other assets use this version." : ""}`
        : `Saved${when}.${facts.feedsOthers ? " Your other assets use it." : ""}`;
      return settle(line, change("revise"), {
        activity,
        edit: facts.reviewed,
      });
    }

    default: {
      const exhaustive: never = facts.status;
      throw new Error(`Unknown asset status: ${String(exhaustive)}`);
    }
  }
}

/** The words My content and a collection's items use. */
export const COLLECTION_COPY = {
  linkedin_posts: {
    newLabel: "New post",
    itemLabel: "LinkedIn post",
    empty: "No posts yet. NOVA writes each one with you, in your voice.",
  },
  articles: {
    newLabel: "New article",
    itemLabel: "Article",
    empty: "No articles yet. NOVA drafts each one with you.",
  },
  client_stories: {
    newLabel: "New story",
    itemLabel: "Client story",
    empty:
      "No client stories yet. NOVA writes each one with you from what changed for a client.",
  },
} as const satisfies Record<
  CollectionAssetKey,
  { newLabel: string; itemLabel: string; empty: string }
>;

/** What one item of an asset is called: "LinkedIn post". */
export function collectionItemLabel(key: AssetKey): string {
  return isCollectionAssetKey(key)
    ? COLLECTION_COPY[key].itemLabel
    : assetByKey(key).label;
}

const ROUTE_TITLE_MAX = 48;

/**
 * An item route's breadcrumb and tab title: the shown version's opening
 * line, truncated, or the item's generic label while there is none to read —
 * no version shown yet, or the detail read failed.
 */
export function postRouteTitle(
  assetKey: AssetKey,
  detail: CoachAssetDetail | null | undefined,
): string {
  const openingLine = detail?.shown ? postOpeningLine(detail.shown.data) : null;
  return openingLine === null
    ? collectionItemLabel(assetKey)
    : truncateTitle(openingLine, ROUTE_TITLE_MAX);
}

/**
 * A collection on My content: open, with its New action, or closed, with the
 * same line and way in the asset page gives a closed asset.
 */
export type CollectionPagePlan =
  | { readonly kind: "open" }
  | {
      readonly kind: "closed";
      readonly line: string;
      readonly action: AssetWayIn | null;
    };

export function collectionPagePlan(
  row: Pick<CoachAssetView, "status" | "target" | "note">,
): CollectionPagePlan {
  const cta = targetCta(row.target);
  if (cta !== null) {
    return {
      kind: "closed",
      line: lockedLine(row.status, row.target),
      action: { kind: "target", label: cta },
    };
  }
  switch (row.status) {
    case "coming_soon":
    case "with_your_trial":
    case "locked_churned":
      return {
        kind: "closed",
        line: lockedLine(row.status, row.target),
        action: null,
      };
    case "not_yet":
      return { kind: "closed", ...notYetPlan(row.note) };
    default:
      return { kind: "open" };
  }
}

/** Where one item of a collection opens. */
export type CollectionItemDestination =
  | {
      readonly kind: "item_page";
      readonly asset: AssetKey;
      readonly itemId: string;
    }
  | { readonly kind: "article"; readonly articleId: string }
  /** Coach testimonials are edited with the offer they sit under. */
  | { readonly kind: "testimonials" };

export interface CollectionItemRow {
  readonly title: string;
  /** What is happening to the item now, under its title. */
  readonly note: string | null;
  readonly chip: { readonly label: string; readonly tone: AssetChipTone };
  readonly destination: CollectionItemDestination | null;
  readonly updatedAt: string;
}

/**
 * The articles surface's own words, so an article reads the same here and on
 * its page.
 */
const CONTENT_STATUS_CHIPS = {
  [ContentStatus.Draft]: { label: "Draft", tone: "progress" },
  [ContentStatus.InReview]: { label: "In review", tone: "muted" },
  [ContentStatus.Published]: { label: "Published", tone: "approved" },
  [ContentStatus.Archived]: { label: "Archived", tone: "muted" },
} as const satisfies Record<
  ContentStatus,
  { label: string; tone: AssetChipTone }
>;

/** The testimonials page's own words. */
const TESTIMONIAL_STATUS_CHIPS = {
  pending: { label: "Awaiting review", tone: "muted" },
  published: { label: "Live on profile", tone: "approved" },
  hidden: { label: "Hidden", tone: "muted" },
} as const satisfies Record<
  Extract<CoachCollectionItem, { kind: "testimonial" }>["status"],
  { label: string; tone: AssetChipTone }
>;

const EDIT_NOTES = {
  [ContentStatus.Draft]: "Your edit is a draft",
  [ContentStatus.InReview]: "Your edit is in review",
  [ContentStatus.Published]: null,
  [ContentStatus.Archived]: null,
} as const satisfies Record<ContentStatus, string | null>;

/** One item, as its row on My content shows it. */
export function collectionItemRow(
  assetKey: AssetKey,
  item: CoachCollectionItem,
): CollectionItemRow {
  switch (item.kind) {
    case "post": {
      const presentation = ASSET_STATUS_PRESENTATION[item.status];
      return {
        title: item.title ?? "Untitled post",
        note: item.working ? ASSET_ACTIVITY_NOTES.working : null,
        chip: { label: presentation.label, tone: presentation.tone },
        destination: { kind: "item_page", asset: assetKey, itemId: item.id },
        updatedAt: item.updatedAt,
      };
    }
    case "article":
      return {
        title: item.title,
        note: item.edit ? EDIT_NOTES[item.edit.status] : null,
        chip: CONTENT_STATUS_CHIPS[item.status],
        // An open edit is where the coach's work on a published article is.
        destination: { kind: "article", articleId: item.edit?.id ?? item.id },
        updatedAt: item.updatedAt,
      };
    case "testimonial":
      return {
        title: `Testimonial from ${item.author}`,
        note: null,
        chip: TESTIMONIAL_STATUS_CHIPS[item.status],
        destination: { kind: "testimonials" },
        updatedAt: item.updatedAt,
      };
    case "story":
      // A success story is reviewed and published by DCM; the coach has no
      // page of her own for it.
      return {
        title: `${item.name}'s story`,
        note: null,
        chip: CONTENT_STATUS_CHIPS[item.status],
        destination: null,
        updatedAt: item.updatedAt,
      };
    default: {
      const exhaustive: never = item;
      throw new Error(`Unknown collection item: ${JSON.stringify(exhaustive)}`);
    }
  }
}

/**
 * The way in for a row a fresh denial has just closed. `open_asset` is what
 * the row's target reads while the cached list still believes access is
 * open — the exact case the denial outran — so it carries no unlock action
 * of its own; billing is where every other access target already sends the
 * coach, and stands in until the invalidated list (`useAssetPageData`, or My
 * content's own invalidation) brings back the real one. Any other target is
 * itself an access target (winback, billing, …) the list had
 * already caught up to, and is kept rather than overwritten.
 */
export function freshDenialTarget(cached: AssetTarget): AssetTarget {
  return cached.kind === "open_asset" ? { kind: "billing" } : cached;
}

/**
 * The asset row an item's page reads, so the asset page's plan works for one
 * post: the item's own status and activity, the collection's target and
 * lock. A closed collection lists no items, so its row is used as it is —
 * unless `readable` says the collection's own fresh read (`getCoachCollection`)
 * has just denied it, over a row that can still sit up to 15 seconds stale
 * (the same race `sectionLocked` guards on My content): losing access must
 * never still look open, so the row is forced closed with a generic lock and
 * a target that always leaves a way out (`freshDenialTarget`), rather than
 * trusting its possibly-stale status and target.
 */
export function collectionItemView(
  row: CoachAssetView,
  item: CoachCollectionItem | null,
  readable: boolean,
): CoachAssetView {
  if (item?.kind === "post") {
    return {
      ...row,
      status: item.status,
      note: { kind: "updated", at: item.updatedAt },
      activity: item.working ? "working" : null,
    };
  }
  return readable
    ? row
    : {
        ...row,
        status: "locked_churned",
        target: freshDenialTarget(row.target),
      };
}
